import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ScrollArea } from "../components/ui/scroll-area";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  FileText, Plus, Download, Calendar, Upload, Trash2, Edit, Eye, Copy,
  Search, Filter, MoreVertical, ChevronDown, ChevronUp, FolderOpen,
  LayoutTemplate, Bold, Italic, Underline, AlignLeft, AlignCenter,
  AlignRight, List, ListOrdered, Type, Heading1, Heading2, Variable
} from "lucide-react";

const DOCUMENT_TYPE_CONFIG = {
  contrat_cdi: { label: 'Contrat CDI', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
  contrat_cdd: { label: 'Contrat CDD', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300' },
  avenant: { label: 'Avenant', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' },
  attestation_employeur: { label: 'Attestation', color: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
  certificat_travail: { label: 'Certificat', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' },
  dpae: { label: 'DPAE', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' },
  courrier: { label: 'Courrier', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  autre: { label: 'Autre', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

const VARIABLE_GROUPS = [
  { title: 'Structure / Entreprise', prefix: 'structure_', icon: '🏢' },
  { title: 'Employé', prefix: 'employee_', icon: '👤' },
  { title: 'Signature / Document', prefix: '', icon: '✍️' }
];

// ==================== HTML EDITOR ====================
const HtmlEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const [isSourceMode, setIsSourceMode] = useState(false);

  const execCommand = (command, val = null) => {
    document.execCommand(command, false, val);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain');
    document.execCommand('insertHTML', false, text);
  };

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value && !isSourceMode) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, isSourceMode]);

  return (
    <div className="border rounded-lg overflow-hidden dark:border-slate-700">
      <div className="bg-slate-100 dark:bg-slate-800 p-2 border-b dark:border-slate-700 flex flex-wrap gap-1">
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('bold')} title="Gras">
          <Bold className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('italic')} title="Italique">
          <Italic className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('underline')} title="Souligné">
          <Underline className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('formatBlock', 'h1')} title="Titre 1">
          <Heading1 className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('formatBlock', 'h2')} title="Titre 2">
          <Heading2 className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('formatBlock', 'p')} title="Paragraphe">
          <Type className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('justifyLeft')} title="Aligner à gauche">
          <AlignLeft className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('justifyCenter')} title="Centrer">
          <AlignCenter className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('justifyRight')} title="Aligner à droite">
          <AlignRight className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 mx-1" />
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('insertUnorderedList')} title="Liste à puces">
          <List className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => execCommand('insertOrderedList')} title="Liste numérotée">
          <ListOrdered className="w-4 h-4" />
        </Button>
        <div className="flex-1" />
        <Button
          type="button"
          variant={isSourceMode ? "default" : "ghost"}
          size="sm"
          onClick={() => setIsSourceMode(!isSourceMode)}
          className={isSourceMode ? "bg-[#0F5156]" : ""}
        >
          &lt;/&gt; HTML
        </Button>
      </div>

      {isSourceMode ? (
        <textarea
          className="w-full h-[350px] p-4 font-mono text-sm resize-none focus:outline-none bg-white dark:bg-slate-950 dark:text-slate-100"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Saisissez votre code HTML ici..."
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onPaste={handlePaste}
          className="w-full min-h-[350px] p-4 focus:outline-none prose prose-sm max-w-none dark:prose-invert bg-white dark:bg-slate-950"
          style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6' }}
          data-placeholder="Commencez à saisir votre document..."
        />
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const Documents = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availableVariables, setAvailableVariables] = useState({});
  const [loading, setLoading] = useState(true);

  // Search, filter, sort
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortOrder, setSortOrder] = useState('date_desc');

  // Section visibility
  const [templatesExpanded, setTemplatesExpanded] = useState(true);
  const [documentsExpanded, setDocumentsExpanded] = useState(true);

  // Variables sidebar
  const [isVariablesOpen, setIsVariablesOpen] = useState(false);
  const [variableSearch, setVariableSearch] = useState('');

  // Generate dialog
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    template_id: '',
    employee_id: '',
    additional_data: {}
  });

  // Template editor dialog
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    nom: '',
    type: 'autre',
    content_html: '',
    variables: []
  });
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // Preview dialog (for template list)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  // Inline preview in template editor
  const [showEditorPreview, setShowEditorPreview] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [docsRes, templatesRes, employeesRes, varsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/variables`, { headers })
      ]);

      if (docsRes.ok) setDocuments(await docsRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (employeesRes.ok) setEmployees(await employeesRes.json());
      if (varsRes.ok) {
        const d = await varsRes.json();
        setAvailableVariables(d.variables || d || {});
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des documents');
      setLoading(false);
    }
  };

  // ==================== FILTERED DATA ====================

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      const matchesSearch = !searchTerm ||
        t.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.type?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || t.type === filterType;
      return matchesSearch && matchesType;
    });
  }, [templates, searchTerm, filterType]);

  const filteredDocuments = useMemo(() => {
    let filtered = documents.filter(d => {
      const matchesSearch = !searchTerm ||
        d.template_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || d.type === filterType;
      return matchesSearch && matchesType;
    });
    if (sortOrder === 'date_desc') filtered.sort((a, b) => new Date(b.generated_date) - new Date(a.generated_date));
    else if (sortOrder === 'date_asc') filtered.sort((a, b) => new Date(a.generated_date) - new Date(b.generated_date));
    else if (sortOrder === 'name_asc') filtered.sort((a, b) => (a.template_name || '').localeCompare(b.template_name || ''));
    return filtered;
  }, [documents, searchTerm, filterType, sortOrder]);

  // ==================== GENERATE DOCUMENT ====================

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!generateForm.template_id || !generateForm.employee_id) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setGenerating(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(generateForm)
      });

      if (response.ok) {
        toast.success('Document généré avec succès');
        setIsGenerateOpen(false);
        setGenerateForm({ template_id: '', employee_id: '', additional_data: {} });
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la génération');
      }
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error(error.message || 'Erreur lors de la génération du document');
    } finally {
      setGenerating(false);
    }
  };

  // ==================== DOWNLOAD DOCUMENT ====================

  const handleDownload = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        const byteCharacters = atob(data.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Document téléchargé');
      } else {
        throw new Error('Erreur téléchargement');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  // ==================== DELETE DOCUMENT ====================

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Supprimer ce document généré ?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Document supprimé');
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ==================== TEMPLATE MANAGEMENT ====================

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ nom: '', type: 'autre', content_html: '', variables: [] });
    setIsTemplateOpen(true);
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      nom: template.nom,
      type: template.type,
      content_html: template.content_html || '',
      variables: template.variables || []
    });
    setIsTemplateOpen(true);
  };

  const openGenerateFromTemplate = (template) => {
    setGenerateForm({ template_id: template.id, employee_id: '', additional_data: {} });
    setIsGenerateOpen(true);
  };

  const handleImportWord = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.docx')) {
      toast.error('Veuillez sélectionner un fichier .docx');
      return;
    }

    setImporting(true);
    try {
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const docx_base64 = await base64Promise;

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/import-docx`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ docx_base64 })
      });

      if (response.ok) {
        const result = await response.json();
        setTemplateForm(prev => ({
          ...prev,
          content_html: result.content_html,
          variables: result.variables || []
        }));
        toast.success(`Contenu Word importé - ${result.variables?.length || 0} variable(s) détectée(s)`);
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur import');
      }
    } catch (error) {
      console.error('Error importing DOCX:', error);
      toast.error(error.message || "Erreur lors de l'import du fichier Word");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.nom || !templateForm.content_html) {
      toast.error('Le nom et le contenu sont requis');
      return;
    }
    setSaving(true);

    try {
      const token = localStorage.getItem('token');

      const varMatches = templateForm.content_html.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
      const variables = [...new Set(varMatches.map(v => v.replace(/\{\{|\}\}/g, '')))];

      if (editingTemplate) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/${editingTemplate.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            nom: templateForm.nom,
            content_html: templateForm.content_html,
            variables: variables
          })
        });
        if (response.ok) {
          toast.success('Template mis à jour');
        } else {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || 'Erreur mise à jour');
        }
      } else {
        const meRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const me = await meRes.json();
        const structure_id = me.structure_id;

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            structure_id: structure_id,
            type: templateForm.type,
            nom: templateForm.nom,
            content_html: templateForm.content_html,
            variables: variables,
            is_default: false
          })
        });
        if (response.ok) {
          toast.success('Template créé');
        } else {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || 'Erreur création');
        }
      }

      setIsTemplateOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Template supprimé');
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePreview = (template) => {
    setPreviewHtml(template.content_html || '');
    setPreviewTitle(template.nom || 'Aperçu');
    setIsPreviewOpen(true);
  };

  const copyVariable = (varName) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    toast.success(`{{${varName}}} copié`);
  };

  const insertVariableInEditor = (varName) => {
    setTemplateForm(prev => ({
      ...prev,
      content_html: prev.content_html + `{{${varName}}}`
    }));
    toast.success(`{{${varName}}} inséré`);
  };

  const getEditorPreviewHtml = () => {
    let preview = templateForm.content_html;
    const sampleValues = {
      'employee_nom': 'DUPONT',
      'employee_prenom': 'Jean',
      'employee_date_naissance': '15/03/1985',
      'employee_lieu_naissance': 'Paris',
      'employee_adresse': '123 rue de la Paix',
      'employee_code_postal': '75001',
      'employee_ville': 'Paris',
      'employee_poste': 'Technicien',
      'employee_departement': 'Production',
      'employee_date_embauche': '01/01/2020',
      'employee_date_entree': '01/01/2020',
      'employee_type_contrat': 'CDI',
      'employee_salaire': '2 500,00',
      'employee_matricule': 'EMP-001',
      'employee_numero_ss': '1 85 03 75 123 456 78',
      'employee_email': 'jean.dupont@exemple.fr',
      'employee_telephone': '06 12 34 56 78',
      'structure_nom': 'Entreprise Exemple SAS',
      'structure_siret': '123 456 789 00012',
      'structure_adresse': '45 avenue de la République',
      'structure_code_postal': '69000',
      'structure_ville': 'Lyon',
      'signature_lieu': 'Lyon',
      'signature_date': new Date().toLocaleDateString('fr-FR'),
      'date_generation': new Date().toLocaleDateString('fr-FR'),
      'responsable_nom': 'M. Martin',
      'responsable_fonction': 'Directeur Général',
    };
    for (const [key, value] of Object.entries(sampleValues)) {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      preview = preview.replace(regex, `<span style="background:#e0f7fa;padding:0 4px;border-radius:2px;">${value}</span>`);
    }
    // Highlight remaining unresolved variables
    preview = preview.replace(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g, '<span style="background:#fff3e0;padding:0 4px;border-radius:2px;color:#e65100;">{{$1}}</span>');
    return preview;
  };

  const getTypeBadge = (type) => {
    const config = DOCUMENT_TYPE_CONFIG[type] || DOCUMENT_TYPE_CONFIG.autre;
    return <Badge className={`${config.color} border-0`}>{config.label}</Badge>;
  };

  const canManageTemplates = ['super_admin', 'admin', 'rh'].includes(user?.role);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F5156] mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* ===== HEADER ===== */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Documents</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {templates.length} template{templates.length > 1 ? 's' : ''} · {documents.length} document{documents.length > 1 ? 's' : ''} généré{documents.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setIsVariablesOpen(true)}>
            <Variable className="w-4 h-4 mr-2" />
            Variables
          </Button>
          {canManageTemplates && (
            <Button variant="outline" onClick={openNewTemplate}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau template
            </Button>
          )}
          <Button className="bg-[#0F5156] hover:bg-[#0a3a3e]" onClick={() => setIsGenerateOpen(true)}>
            <FileText className="w-4 h-4 mr-2" />
            Générer un document
          </Button>
        </div>
      </div>

      {/* ===== SEARCH & FILTER BAR ===== */}
      <Card className="mb-6">
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un template ou document..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {Object.entries(DOCUMENT_TYPE_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Tri" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">Plus récent</SelectItem>
                <SelectItem value="date_asc">Plus ancien</SelectItem>
                <SelectItem value="name_asc">Nom A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ===== TEMPLATES SECTION ===== */}
      <Card className="mb-6">
        <CardHeader
          className="cursor-pointer select-none hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
          onClick={() => setTemplatesExpanded(!templatesExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <LayoutTemplate className="w-5 h-5 text-[#0F5156]" />
              Templates
              <Badge variant="secondary" className="ml-1">{filteredTemplates.length}</Badge>
            </CardTitle>
            {templatesExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {templatesExpanded && (
          <CardContent className="pt-0">
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-8">
                <LayoutTemplate className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400 mb-3">
                  {searchTerm || filterType !== 'all' ? 'Aucun template correspondant' : 'Aucun template configuré'}
                </p>
                {canManageTemplates && !searchTerm && filterType === 'all' && (
                  <Button onClick={openNewTemplate} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-1" /> Créer un template
                  </Button>
                )}
              </div>
            ) : (
              <div className="rounded-md border dark:border-slate-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                      <TableHead>Nom</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="hidden sm:table-cell">Variables</TableHead>
                      <TableHead className="hidden md:table-cell">Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTemplates.map(template => (
                      <TableRow key={template.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#0F5156] flex-shrink-0" />
                            {template.nom}
                          </div>
                        </TableCell>
                        <TableCell>{getTypeBadge(template.type)}</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <span className="text-sm text-slate-500 dark:text-slate-400">
                            {template.variables?.length || 0} variable{(template.variables?.length || 0) > 1 ? 's' : ''}
                          </span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {template.is_default && (
                            <Badge variant="outline" className="text-green-600 border-green-300 dark:text-green-400 dark:border-green-700">
                              Par défaut
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handlePreview(template)} title="Aperçu">
                              <Eye className="w-4 h-4" />
                            </Button>
                            {template.content_html && (
                              <Button size="sm" variant="ghost" onClick={() => openGenerateFromTemplate(template)} title="Générer un document" className="text-[#0F5156]">
                                <FileText className="w-4 h-4" />
                              </Button>
                            )}
                            {canManageTemplates && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditTemplate(template)}>
                                    <Edit className="w-4 h-4 mr-2" /> Modifier
                                  </DropdownMenuItem>
                                  {!template.is_default && (
                                    <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteTemplate(template.id)}>
                                      <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ===== DOCUMENTS GENERES SECTION ===== */}
      <Card className="mb-6">
        <CardHeader
          className="cursor-pointer select-none hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
          onClick={() => setDocumentsExpanded(!documentsExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FolderOpen className="w-5 h-5 text-[#0F5156]" />
              Documents générés
              <Badge variant="secondary" className="ml-1">{filteredDocuments.length}</Badge>
            </CardTitle>
            {documentsExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {documentsExpanded && (
          <CardContent className="pt-0">
            {filteredDocuments.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400 mb-3">
                  {searchTerm || filterType !== 'all' ? 'Aucun document correspondant' : 'Aucun document généré'}
                </p>
                {!searchTerm && filterType === 'all' && (
                  <Button onClick={() => setIsGenerateOpen(true)} className="bg-[#0F5156] hover:bg-[#0a3a3e]" size="sm">
                    Générer votre premier document
                  </Button>
                )}
              </div>
            ) : (
              <div className="rounded-md border dark:border-slate-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                      <TableHead>Document</TableHead>
                      <TableHead>Employé</TableHead>
                      <TableHead className="hidden sm:table-cell">Type</TableHead>
                      <TableHead className="hidden md:table-cell">Date</TableHead>
                      <TableHead className="hidden lg:table-cell">Généré par</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocuments.map(doc => (
                      <TableRow key={doc.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
                            <span className="font-medium">{doc.template_name}</span>
                            <Badge variant="outline" className="text-xs text-red-600 border-red-200 dark:text-red-400 dark:border-red-800">PDF</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-700 dark:text-slate-300">{doc.employee_name}</TableCell>
                        <TableCell className="hidden sm:table-cell">{getTypeBadge(doc.type)}</TableCell>
                        <TableCell className="hidden md:table-cell text-slate-500 dark:text-slate-400">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(doc.generated_date).toLocaleDateString('fr-FR')}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-slate-500 dark:text-slate-400">
                          {doc.generated_by_name}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleDownload(doc.id)} title="Télécharger">
                              <Download className="w-4 h-4" />
                            </Button>
                            {canManageTemplates && (
                              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteDocument(doc.id)} title="Supprimer">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ===== VARIABLES SIDEBAR (Sheet) ===== */}
      <Sheet open={isVariablesOpen} onOpenChange={setIsVariablesOpen}>
        <SheetContent side="right" className="w-[400px] sm:w-[450px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Variable className="w-5 h-5 text-[#0F5156]" />
              Variables disponibles
            </SheetTitle>
            <SheetDescription>
              Utilisez ces variables dans vos templates avec la syntaxe {"{{variable}}"}. Cliquez pour copier.
            </SheetDescription>
          </SheetHeader>
          <Separator className="my-4" />
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher une variable..."
                value={variableSearch}
                onChange={(e) => setVariableSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <ScrollArea className="h-[calc(100vh-220px)]">
            <div className="space-y-6 pr-4">
              {VARIABLE_GROUPS.map(group => {
                const vars = Object.entries(availableVariables).filter(([key]) => {
                  if (group.prefix === '') {
                    return !key.startsWith('structure_') && !key.startsWith('employee_');
                  }
                  return key.startsWith(group.prefix);
                }).filter(([key, desc]) => {
                  if (!variableSearch) return true;
                  const search = variableSearch.toLowerCase();
                  return key.toLowerCase().includes(search) || desc.toLowerCase().includes(search);
                });
                if (vars.length === 0) return null;
                return (
                  <div key={group.title}>
                    <h3 className="font-semibold text-sm text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                      <span>{group.icon}</span> {group.title}
                      <Badge variant="secondary" className="text-xs">{vars.length}</Badge>
                    </h3>
                    <div className="space-y-1">
                      {vars.map(([key, desc]) => (
                        <div
                          key={key}
                          className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                          onClick={() => copyVariable(key)}
                          title="Cliquer pour copier"
                        >
                          <code className="bg-white dark:bg-slate-900 px-2 py-0.5 rounded border dark:border-slate-700 text-xs font-mono text-blue-700 dark:text-blue-400 flex-shrink-0">
                            {`{{${key}}}`}
                          </code>
                          <span className="text-sm text-slate-600 dark:text-slate-400 flex-1 truncate">{desc}</span>
                          <Copy className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* ===== GENERATE DIALOG ===== */}
      <Dialog open={isGenerateOpen} onOpenChange={(open) => {
        setIsGenerateOpen(open);
        if (!open) setGenerateForm({ template_id: '', employee_id: '', additional_data: {} });
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Générer un document</DialogTitle>
            <DialogDescription>
              Sélectionnez un template et un employé pour générer le document PDF.
              Les variables du template seront automatiquement remplies avec les données de l'employé et de la structure.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleGenerate} className="space-y-4">
            <div>
              <Label>Template</Label>
              <Select
                value={generateForm.template_id}
                onValueChange={(value) => setGenerateForm({...generateForm, template_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez un template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.content_html).map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center gap-2">
                        <span>{template.nom}</span>
                        <span className="text-xs text-slate-400">({DOCUMENT_TYPE_CONFIG[template.type]?.label || template.type})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Employé</Label>
              <Select
                value={generateForm.employee_id}
                onValueChange={(value) => setGenerateForm({...generateForm, employee_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez un employé" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.prenom} {employee.nom}{employee.poste ? ` - ${employee.poste}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Variables info */}
            {generateForm.template_id && (() => {
              const sel = templates.find(t => t.id === generateForm.template_id);
              if (!sel) return null;

              const structureVars = (sel.variables || []).filter(v => v.startsWith('structure_'));
              const employeeVars = (sel.variables || []).filter(v => v.startsWith('employee_'));
              const autoVars = (sel.variables || []).filter(v => v.startsWith('signature_') || v === 'date_fin_effective');
              const customVars = (sel.variables || []).filter(v =>
                !v.startsWith('structure_') && !v.startsWith('employee_') &&
                !v.startsWith('signature_') && v !== 'date_fin_effective'
              );

              return (
                <div className="space-y-3">
                  {/* Auto-filled variables summary */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">Variables remplies automatiquement</p>
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      {structureVars.length > 0 && (
                        <Badge variant="outline" className="border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400">
                          {structureVars.length} structure
                        </Badge>
                      )}
                      {employeeVars.length > 0 && (
                        <Badge variant="outline" className="border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400">
                          {employeeVars.length} employé
                        </Badge>
                      )}
                      {autoVars.length > 0 && (
                        <Badge variant="outline" className="border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400">
                          {autoVars.length} signature/date
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Custom variables inputs */}
                  {customVars.length > 0 && (
                    <div className="space-y-2">
                      <Label>Données supplémentaires</Label>
                      {customVars.map(varName => (
                        <div key={varName}>
                          <Label className="text-xs text-slate-500">{varName}</Label>
                          <Input
                            value={generateForm.additional_data[varName] || ''}
                            onChange={(e) => setGenerateForm({
                              ...generateForm,
                              additional_data: {
                                ...generateForm.additional_data,
                                [varName]: e.target.value
                              }
                            })}
                            placeholder={availableVariables[varName] || varName}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="bg-[#0F5156] hover:bg-[#0a3a3e]" disabled={generating || !generateForm.template_id || !generateForm.employee_id}>
                {generating ? 'Génération...' : 'Générer le PDF'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsGenerateOpen(false)}>
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== TEMPLATE EDITOR DIALOG ===== */}
      <Dialog open={isTemplateOpen} onOpenChange={(open) => {
        setIsTemplateOpen(open);
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Modifier le template' : 'Nouveau template'}</DialogTitle>
            <DialogDescription>
              Définissez le contenu du template avec l'éditeur visuel ou en HTML. Utilisez {"{{variable}}"} pour les champs dynamiques.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveTemplate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom du template *</Label>
                <Input
                  value={templateForm.nom}
                  onChange={(e) => setTemplateForm({...templateForm, nom: e.target.value})}
                  placeholder="Ex: Contrat CDI standard"
                  required
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={templateForm.type}
                  onValueChange={(value) => setTemplateForm({...templateForm, type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DOCUMENT_TYPE_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Import Word + Insert variable row */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md flex-1 min-w-[280px]">
                <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Importer depuis Word</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">Convertit le .docx en HTML</p>
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".docx"
                    onChange={handleImportWord}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/50"
                  >
                    {importing ? 'Import...' : 'Choisir .docx'}
                  </Button>
                </div>
              </div>

              {/* Insert variable button */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/50">
                    <Variable className="w-4 h-4 mr-2" />
                    Insérer une variable
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[380px] max-h-[400px] overflow-y-auto" align="end">
                  <div className="space-y-4">
                    {VARIABLE_GROUPS.map(group => {
                      const vars = Object.entries(availableVariables).filter(([key]) => {
                        if (group.prefix === '') {
                          return !key.startsWith('structure_') && !key.startsWith('employee_');
                        }
                        return key.startsWith(group.prefix);
                      });
                      if (vars.length === 0) return null;
                      return (
                        <div key={group.title}>
                          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">{group.icon} {group.title}</p>
                          <div className="flex flex-wrap gap-1">
                            {vars.map(([key]) => (
                              <button
                                key={key}
                                type="button"
                                className="text-xs px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 hover:bg-[#0F5156]/10 dark:hover:bg-[#0F5156]/30 text-slate-700 dark:text-slate-300 hover:text-[#0F5156] dark:hover:text-[#0F5156] transition-colors font-mono"
                                onClick={() => insertVariableInEditor(key)}
                              >
                                {`{{${key}}}`}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Content editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Contenu du template *</Label>
                <Button
                  type="button"
                  variant={showEditorPreview ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowEditorPreview(!showEditorPreview)}
                  className={showEditorPreview ? "bg-[#0F5156] hover:bg-[#0a3a3e]" : ""}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showEditorPreview ? "Masquer l'aperçu" : "Aperçu"}
                </Button>
              </div>
              <HtmlEditor
                value={templateForm.content_html}
                onChange={(html) => setTemplateForm({...templateForm, content_html: html})}
              />
            </div>

            {/* Inline preview */}
            {showEditorPreview && templateForm.content_html && (
              <Card className="border-[#0F5156]/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Eye className="w-4 h-4 text-[#0F5156]" />
                    Aperçu du document (valeurs d'exemple)
                  </CardTitle>
                  <p className="text-xs text-slate-500">Les variables sont remplacées par des valeurs fictives. Les valeurs en <span style={{background:'#e0f7fa', padding:'0 4px', borderRadius:'2px'}}>bleu</span> sont résolues, celles en <span style={{background:'#fff3e0', padding:'0 4px', borderRadius:'2px', color:'#e65100'}}>orange</span> ne sont pas reconnues.</p>
                </CardHeader>
                <CardContent>
                  <div
                    className="border rounded p-6 bg-white dark:bg-slate-950 max-h-[400px] overflow-auto shadow-inner"
                    dangerouslySetInnerHTML={{ __html: getEditorPreviewHtml() }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Detected variables info */}
            {templateForm.content_html && (() => {
              const matches = templateForm.content_html.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
              const uniqueVars = [...new Set(matches.map(v => v.replace(/\{\{|\}\}/g, '')))];
              if (uniqueVars.length === 0) return null;

              const structVars = uniqueVars.filter(v => v.startsWith('structure_'));
              const empVars = uniqueVars.filter(v => v.startsWith('employee_'));
              const otherVars = uniqueVars.filter(v => !v.startsWith('structure_') && !v.startsWith('employee_'));

              return (
                <div className="p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                    {uniqueVars.length} variable{uniqueVars.length > 1 ? 's' : ''} détectée{uniqueVars.length > 1 ? 's' : ''}
                  </p>
                  <div className="space-y-1.5">
                    {structVars.length > 0 && (
                      <div>
                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">Structure : </span>
                        <span className="text-xs text-green-700 dark:text-green-300">{structVars.map(v => `{{${v}}}`).join(', ')}</span>
                      </div>
                    )}
                    {empVars.length > 0 && (
                      <div>
                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">Employé : </span>
                        <span className="text-xs text-green-700 dark:text-green-300">{empVars.map(v => `{{${v}}}`).join(', ')}</span>
                      </div>
                    )}
                    {otherVars.length > 0 && (
                      <div>
                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">Autres : </span>
                        <span className="text-xs text-green-700 dark:text-green-300">{otherVars.map(v => `{{${v}}}`).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="bg-[#0F5156] hover:bg-[#0a3a3e]" disabled={saving}>
                {saving ? 'Enregistrement...' : (editingTemplate ? 'Mettre à jour' : 'Créer le template')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsTemplateOpen(false)}>
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== PREVIEW DIALOG ===== */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Aperçu — {previewTitle}</DialogTitle>
          </DialogHeader>
          <div className="border dark:border-slate-700 rounded p-6 bg-white dark:bg-slate-950 overflow-auto max-h-[70vh]">
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
